import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faChevronDown, faSlidersH, faCheck } from '@fortawesome/pro-light-svg-icons';
import './SpotlightsMenus.scss';

// Shared "close on outside click" behavior for all three menus. Mirrors the
// pattern used in the user-facing PartnerPortalSpotlights / DiscoverV3 menus.
const useClickOutside = (onClose, active) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [active, onClose]);
  return ref;
};

// ── Row action ellipsis/kebab menu ──
// options: [{ label, icon?, onClick, danger?, disabled? }] (falsy entries are skipped)
// The dropdown is rendered in a portal with fixed positioning so it isn't clipped by the
// `overflow: hidden` on expanded table rows / scroll containers.
export const SpotlightsActionMenu = ({ options = [] }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const visible = options.filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = e => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    // Recomputing on scroll/resize is more work than it's worth — just close instead.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (!visible.length) return null;

  const toggle = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      // Anchor the menu's right edge to the trigger's right edge, just below it.
      setCoords({ top: Math.round(r.bottom + 6), right: Math.round(window.innerWidth - r.right) });
    }
    setOpen(o => !o);
  };

  return (
    <div className='sl-menu' ref={triggerRef} onClick={e => e.stopPropagation()}>
      <button className={cn('sl-menu-trigger', { open })} onClick={toggle} title='Actions'>
        <FontAwesomeIcon icon={faEllipsisH} />
      </button>
      {open &&
        coords &&
        ReactDOM.createPortal(
          <div className='sl-menu-dropdown portal' ref={dropdownRef} style={{ top: coords.top, right: coords.right }} onClick={e => e.stopPropagation()}>
            {visible.map((o, i) => (
              <button
                key={i}
                className={cn('sl-menu-item', { danger: o.danger, disabled: o.disabled })}
                disabled={o.disabled}
                onClick={() => {
                  if (o.disabled) return;
                  setOpen(false);
                  o.onClick && o.onClick();
                }}
              >
                {o.icon && <FontAwesomeIcon icon={o.icon} className='sl-menu-item-icon' />}
                <span>{o.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

// ── Sort menu ──
// options: [{ key, label }]; value=current key; dir='asc'|'desc'; onChange(key, dir)
export const SpotlightsSortMenu = ({ options = [], value, dir, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false), open);
  const current = options.find(o => o.key === value);

  return (
    <div className='sl-sort' ref={ref}>
      <button className={cn('sl-sort-trigger', { open })} onClick={() => setOpen(o => !o)}>
        <span className='sl-sort-trigger-label'>
          Sort: {current ? current.label : '—'} {dir === 'asc' ? '↑' : '↓'}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={cn('sl-sort-chevron', { open })} />
      </button>
      {open && (
        <div className='sl-sort-panel'>
          {options.map(o => {
            const selected = o.key === value;
            return (
              <button
                key={o.key}
                className={cn('sl-sort-option', { selected })}
                onClick={() => {
                  // Re-selecting the active option flips direction; a new option defaults to desc.
                  const nextDir = selected ? (dir === 'asc' ? 'desc' : 'asc') : 'desc';
                  onChange(o.key, nextDir);
                  setOpen(false);
                }}
              >
                <span className='sl-sort-option-name'>{o.label}</span>
                {selected && <span className='sl-sort-option-dir'>{dir === 'asc' ? 'Asc ↑' : 'Desc ↓'}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Filter menu (generic panel) ──
// count = number of active filters (shows a badge); onClear optional; children = the filter fields.
export const SpotlightsFilterMenu = ({ count = 0, onClear, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false), open);

  return (
    <div className='sl-filter' ref={ref}>
      <button className={cn('sl-filter-trigger', { active: count > 0 || open })} onClick={() => setOpen(o => !o)}>
        <FontAwesomeIcon icon={faSlidersH} className='sl-filter-icon' />
        <span>Filters</span>
        {count > 0 && <span className='sl-filter-count'>{count}</span>}
        <FontAwesomeIcon icon={faChevronDown} className={cn('sl-filter-chevron', { open })} />
      </button>
      {open && (
        <div className='sl-filter-panel'>
          {children}
          {count > 0 && onClear && (
            <button
              className='sl-filter-clear'
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// A single filter field inside the filter panel (label + control).
export const FilterField = ({ label, children }) => (
  <div className='sl-filter-field'>
    {label && <div className='sl-filter-field-label'>{label}</div>}
    {children}
  </div>
);

// A selectable option row used inside a FilterField (single-select list).
export const FilterOption = ({ label, selected, onClick }) => (
  <button className={cn('sl-filter-option', { selected })} onClick={onClick}>
    <span>{label}</span>
    {selected && <FontAwesomeIcon icon={faCheck} />}
  </button>
);

// ── Generic icon-triggered popover (e.g. a settings gear with arbitrary panel content) ──
export const SpotlightsPopover = ({ icon, label, align = 'right', children }) => {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false), open);
  return (
    <div className='sl-popover' ref={ref}>
      <button className={cn('sl-popover-trigger', { open })} onClick={() => setOpen(o => !o)}>
        {icon && <FontAwesomeIcon icon={icon} />}
        {label && <span>{label}</span>}
      </button>
      {open && <div className={cn('sl-popover-panel', align)}>{children}</div>}
    </div>
  );
};
